import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import {
  CreateMerchantUserPayload,
  merchantUsersApi,
  Role,
} from 'api/merchantUsers'
import { Role as RbacRole, rolesApi } from 'api/roles'
import Formsy from 'formsy-react'
import useFlag from 'hooks/useFlag'
import { useIsRbacAdmin } from 'hooks/useIsRbacAdmin'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import { useDisplayMessage } from '../../hooks/useDisplayMessage'
import { buildTranslate } from '../../locales'
import EmailCard from './EmailCard'
import FormLayout from './FormLayout'
import { isNewEmailDomain } from './helpers'
import MerchantUserForm from './MerchantUserForm'
import NewDomainWarningModal from './NewDomainWarningModal'
import { RbacLocationsSection } from './RbacLocationsSection'
import { RbacRoleDropdown } from './RbacRoleDropdown'

const t = buildTranslate('merchant_users')

const NewMerchantUser = () => {
  const dispatch = useDispatch()
  const rbacEnabled = useFlag('role-based-access-control', false)
  const isAdmin = useIsRbacAdmin()

  const [createMerchantUser, { reset, isLoading: isCreating }] =
    merchantUsersApi.useCreateMerchantUserMutation({
      fixedCacheKey: 'create',
    })

  const displayMessage = useDisplayMessage(reset)

  const {
    data: merchantUsersData,
    isSuccess: merchantUsersLoaded,
    isError: merchantUsersError,
  } = merchantUsersApi.useGetMerchantUsersQuery({})
  const existingEmails = useMemo(
    () => (merchantUsersData?.merchant_users ?? []).map(user => user.email),
    [merchantUsersData]
  )
  // Gate submission on a settled baseline. Until the query resolves,
  // existingEmails is empty, and an empty list must not be read as "no existing
  // users" — that would skip the warning for a genuinely new domain. On error
  // we let the invite through rather than hard-blocking on a secondary query.
  const baselineReady = merchantUsersLoaded || merchantUsersError
  const [showDomainWarning, setShowDomainWarning] = useState(false)

  const [merchantUser, setMerchantUser] = useState<CreateMerchantUserPayload>({
    email: '',
    role: 'admin',
    location_ids: [],
  })

  const [rbacEmail, setRbacEmail] = useState('')
  const [selectedRbacRoleIds, setSelectedRbacRoleIds] = useState<number[]>([])
  const [rbacLocationIds, setRbacLocationIds] = useState<number[]>([])

  const { data: rolesData } = rolesApi.useGetRolesQuery(undefined, {
    skip: !rbacEnabled,
  })
  const allRoles = rolesData?.roles ?? []

  const selectedRbacRoles: RbacRole[] = selectedRbacRoleIds
    .map(id => allRoles.find(r => r.id === id))
    .filter((r): r is RbacRole => Boolean(r))

  const hasCustomRole = selectedRbacRoles.some(r => !r.preset)

  useEffect(() => {
    if (allRoles.length === 0 || selectedRbacRoleIds.length === 0) return
    if (!hasCustomRole) {
      setRbacLocationIds([])
    }
  }, [hasCustomRole, allRoles.length, selectedRbacRoleIds.length])

  const [css] = useStyletron()

  const handleCreateError = (err: any) => {
    if (err.status === 422) {
      displayMessage(t('new.already_invited'), 'danger')
    } else {
      displayMessage(t('new.error_generic'), 'danger')
    }
  }

  if (rbacEnabled) {
    const createRbacUser = async () => {
      try {
        await createMerchantUser({
          email: rbacEmail,
          rbac_role_ids: selectedRbacRoleIds,
          location_ids: rbacLocationIds,
        }).unwrap()
        dispatch(push('/merchant_users?tab=users'))
      } catch (err: any) {
        handleCreateError(err)
      }
    }

    const handleRbacFinish = async () => {
      if (selectedRbacRoleIds.length === 0) return
      if (isNewEmailDomain(rbacEmail, existingEmails)) {
        setShowDomainWarning(true)
        return
      }
      await createRbacUser()
    }

    return (
      <FormLayout
        onFinish={handleRbacFinish}
        canProceed={
          Boolean(rbacEmail && selectedRbacRoleIds.length > 0) && baselineReady
        }
        hideFinish={!isAdmin}
      >
        <Formsy role="form">
          {!isAdmin && (
            <div className="mb-l">
              <Notification kind="warning" size="full">
                <div className="d-flex p-xs align-items-center">
                  <i
                    className="fa fa-exclamation-triangle mr-xs"
                    aria-hidden="true"
                  />
                  <Text.BodyText4>
                    {t('edit.admin_required_warning')}
                  </Text.BodyText4>
                </div>
              </Notification>
            </div>
          )}
          <EmailCard
            value={rbacEmail}
            onChange={value => setRbacEmail(value)}
          />
          <div className={css({ marginTop: '40px' })}>
            <RbacRoleDropdown
              selectedRoleIds={selectedRbacRoleIds}
              onChange={setSelectedRbacRoleIds}
              locationIds={rbacLocationIds}
              disabled={!isAdmin}
            />
            <RbacLocationsSection
              roles={selectedRbacRoles}
              locationIds={rbacLocationIds}
              onChange={setRbacLocationIds}
              disabled={!isAdmin}
            />
          </div>
        </Formsy>
        <NewDomainWarningModal
          isOpen={showDomainWarning}
          isLoading={isCreating}
          onClose={() => setShowDomainWarning(false)}
          onProceed={async () => {
            setShowDomainWarning(false)
            await createRbacUser()
          }}
        />
      </FormLayout>
    )
  }

  const createUser = async () => {
    try {
      await createMerchantUser(merchantUser).unwrap()
      dispatch(push('/merchant_users?tab=users'))
    } catch (err: any) {
      handleCreateError(err)
    }
  }

  const handleFinish = async () => {
    if (isNewEmailDomain(merchantUser.email, existingEmails)) {
      setShowDomainWarning(true)
      return
    }
    await createUser()
  }

  return (
    <FormLayout
      onFinish={handleFinish}
      canProceed={Boolean(merchantUser.email) && baselineReady}
    >
      <Formsy role="form">
        <EmailCard
          value={merchantUser.email}
          onChange={value => {
            setMerchantUser(prev => ({ ...prev, email: value }))
          }}
        />
        <Text.Header3
          className={css({
            marginTop: '40px',
            marginBottom: '20px',
          })}
        >
          {t('form.access.header')}
        </Text.Header3>
        <MerchantUserForm
          merchantUser={merchantUser}
          onChange={nextMerchantUser => {
            setMerchantUser(prev => ({
              ...prev,
              location_ids: nextMerchantUser.location_ids,
              role: nextMerchantUser.role as Role,
            }))
          }}
        />
      </Formsy>
      <NewDomainWarningModal
        isOpen={showDomainWarning}
        isLoading={isCreating}
        onClose={() => setShowDomainWarning(false)}
        onProceed={async () => {
          setShowDomainWarning(false)
          await createUser()
        }}
      />
    </FormLayout>
  )
}

export default NewMerchantUser
